import { useEffect, useState, useCallback } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable } from "@dnd-kit/core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Edit, Link as LinkIcon, Trash2, Square, Circle, RectangleHorizontal, Armchair } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Draggable Table Component
const DraggableTable = ({ table, onStatusChange, onEdit, isSelected, onSelect }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: table.id,
  });

  const style = {
    position: 'absolute',
    left: `${table.x}px`,
    top: `${table.y}px`,
    transform: transform 
      ? `translate(calc(-50% + ${transform.x}px), calc(-50% + ${transform.y}px))` 
      : 'translate(-50%, -50%)',
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "free": return "#D4AF37";
      case "occupied": return "#B87333";
      case "dessert": return "#F0C74A";
      case "bill": return "#8C5828";
      case "reserved": return "#6366F1";
      default: return "#D4AF37";
    }
  };

  // Determine size based on type
  const isBarSeat = table.type === 'bar';
  const isDecor = table.type === 'decor';
  
  const shapeClass = table.shape === 'circular' ? 'rounded-full' : 
                     table.shape === 'square' ? 'rounded-md' : 'rounded-lg';
  
  let width, height;
  if (isDecor) {
    width = table.width || '120px';
    height = table.height || '60px';
  } else if (isBarSeat) {
    width = '40px';
    height = '40px';
  } else {
    // Square tables are now smaller (60x60 instead of 90x90)
    width = table.shape === 'circular' ? '80px' : 
            table.shape === 'square' ? '60px' : '100px';
    height = table.shape === 'circular' ? '80px' : 
             table.shape === 'square' ? '60px' : '65px';
  }

  const prefix = isBarSeat ? 'B' : isDecor ? '' : 'T';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        if (e.shiftKey && !isDecor) {
          e.stopPropagation();
          onSelect(table.id);
        }
      }}
      data-testid={`table-${table.number}`}
      className="touch-none"
    >
      <div
        className={`${shapeClass} ${isDecor ? 'bg-[#2A2520]' : 'bg-[#1A1A1A]'} border-3 p-2 hover:scale-105 transition-all shadow-lg cursor-move relative ${
          isSelected ? 'ring-2 ring-brand-gold ring-offset-2 ring-offset-[#0A0A0A]' : ''
        }`}
        style={{
          borderColor: isDecor ? '#4A4540' : getStatusColor(table.status),
          borderWidth: '3px',
          width: width,
          height: height,
          borderStyle: isDecor ? 'dashed' : 'solid',
        }}
      >
        <div className="text-center flex flex-col justify-center h-full">
          {isDecor ? (
            <div className="font-medium text-gray-500 text-xs">{table.name || 'Decor'}</div>
          ) : (
            <>
              <div className="font-bold text-brand-beige text-lg" data-testid={`table-number-${table.number}`}>
                {prefix}{table.number}
              </div>
              <div className="text-xs text-gray-300">{table.capacity}p</div>
              {!isBarSeat && (
                <select
                  value={table.status}
                  onChange={(e) => {
                    e.stopPropagation();
                    onStatusChange(table.id, e.target.value);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="mt-1 text-[10px] bg-transparent text-gray-300 border-0 p-0 cursor-pointer w-full"
                  data-testid={`table-status-select-${table.number}`}
                >
                  <option value="free">Vrij</option>
                  <option value="reserved">Geres.</option>
                  <option value="occupied">Bezet</option>
                  <option value="dessert">Dessert</option>
                  <option value="bill">Rekening</option>
                </select>
              )}
            </>
          )}
        </div>
        {!isDecor && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(table);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute -top-1 -right-1 p-1 bg-[#333] rounded-full hover:bg-brand-gold/40 transition-colors"
            data-testid={`edit-table-${table.number}`}
          >
            <Edit className="w-3 h-3 text-brand-beige" />
          </button>
        )}
        {isDecor && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(table);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute -top-1 -right-1 p-1 bg-[#333] rounded-full hover:bg-red-500/40 transition-colors"
          >
            <Trash2 className="w-3 h-3 text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
};

const TableManagement = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTables, setSelectedTables] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDecorDialog, setShowDecorDialog] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [newTable, setNewTable] = useState({
    number: '',
    shape: 'rectangular',
    capacity: 4,
    type: 'table'
  });
  const [newDecor, setNewDecor] = useState({
    name: 'Bar',
    width: 150,
    height: 50
  });

  // Better sensor config for smoother dragging
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3, // Lower distance for easier activation
      },
    })
  );

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`);
      setTables(response.data);
    } catch (error) {
      console.error("Error fetching tables:", error);
      toast.error("Error loading tables");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = useCallback(async (event) => {
    const { active, delta } = event;
    const tableId = active.id;
    const table = tables.find(t => t.id === tableId);
    
    if (!table) return;

    // Calculate new position without snapping
    const newX = Math.max(50, Math.min(table.x + delta.x, 1150));
    const newY = Math.max(50, Math.min(table.y + delta.y, 550));

    // Optimistic update for smoother UX
    setTables(prev => prev.map(t => 
      t.id === tableId ? { ...t, x: newX, y: newY } : t
    ));

    try {
      await axios.put(`${API}/tables/${tableId}`, {
        x: newX,
        y: newY
      });
    } catch (error) {
      console.error("Error updating table position:", error);
      toast.error("Error updating table position");
      fetchTables();
    }
  }, [tables]);

  const addTable = async () => {
    if (!newTable.number || newTable.number <= 0) {
      toast.error("Geef een geldig nummer op");
      return;
    }
    
    const prefix = newTable.type === 'bar' ? 'B' : 'T';
    const existingWithSameNumber = tables.find(t => 
      t.number === parseInt(newTable.number) && t.type === newTable.type
    );
    
    if (existingWithSameNumber) {
      toast.error(`${prefix}${newTable.number} bestaat al`);
      return;
    }
    
    const existingCount = tables.filter(t => t.type !== 'decor').length;
    const baseX = 150;
    const baseY = 150;
    const offsetX = (existingCount % 6) * 130;
    const offsetY = Math.floor(existingCount / 6) * 110;
    
    try {
      await axios.post(`${API}/tables`, {
        number: parseInt(newTable.number),
        capacity: newTable.type === 'bar' ? 1 : newTable.capacity,
        shape: newTable.type === 'bar' ? 'square' : newTable.shape,
        type: newTable.type,
        x: baseX + offsetX,
        y: baseY + offsetY
      });
      
      await fetchTables();
      
      toast.success(`${prefix}${newTable.number} toegevoegd`);
      setShowAddDialog(false);
      setNewTable({ number: '', shape: 'rectangular', capacity: 4, type: 'table' });
    } catch (error) {
      console.error("Error adding table:", error);
      toast.error("Error adding table");
    }
  };

  const addDecorElement = async () => {
    if (!newDecor.name) {
      toast.error("Geef een naam op");
      return;
    }
    
    const existingDecors = tables.filter(t => t.type === 'decor').length;
    
    try {
      await axios.post(`${API}/tables`, {
        number: 900 + existingDecors,
        name: newDecor.name,
        type: 'decor',
        shape: 'rectangular',
        capacity: 0,
        width: `${newDecor.width}px`,
        height: `${newDecor.height}px`,
        x: 400,
        y: 300
      });
      
      await fetchTables();
      
      toast.success(`${newDecor.name} toegevoegd`);
      setShowDecorDialog(false);
      setNewDecor({ name: 'Bar', width: 150, height: 50 });
    } catch (error) {
      console.error("Error adding decor:", error);
      toast.error("Error adding decor element");
    }
  };

  const updateDecorElement = async () => {
    if (!editingTable || editingTable.type !== 'decor') return;
    
    try {
      await axios.put(`${API}/tables/${editingTable.id}`, {
        name: editingTable.name,
        width: editingTable.width,
        height: editingTable.height
      });
      
      setTables(tables.map(t => 
        t.id === editingTable.id ? editingTable : t
      ));
      toast.success(`${editingTable.name} bijgewerkt`);
      setEditingTable(null);
    } catch (error) {
      console.error("Error updating decor:", error);
      toast.error("Error updating decor element");
    }
  };

  const updateTable = async () => {
    if (!editingTable) return;
    
    const prefix = editingTable.type === 'bar' ? 'B' : 'T';
    const originalTable = tables.find(t => t.id === editingTable.id);
    if (editingTable.number !== originalTable.number) {
      const existingWithSameNumber = tables.find(t => 
        t.number === editingTable.number && 
        t.type === editingTable.type && 
        t.id !== editingTable.id
      );
      if (existingWithSameNumber) {
        toast.error(`${prefix}${editingTable.number} bestaat al`);
        return;
      }
    }
    
    try {
      await axios.put(`${API}/tables/${editingTable.id}`, {
        number: editingTable.number,
        capacity: editingTable.capacity,
        shape: editingTable.shape,
        min_party: editingTable.min_party,
        max_party: editingTable.max_party
      });
      
      setTables(tables.map(t => 
        t.id === editingTable.id ? editingTable : t
      ));
      toast.success("Tafel bijgewerkt");
      setEditingTable(null);
    } catch (error) {
      console.error("Error updating table:", error);
      toast.error("Error updating table");
    }
  };

  const deleteTable = async (tableId) => {
    if (!window.confirm("Weet u zeker dat u dit wilt verwijderen?")) return;
    
    try {
      await axios.delete(`${API}/tables/${tableId}`);
      setTables(tables.filter(t => t.id !== tableId));
      setEditingTable(null);
      toast.success("Verwijderd");
    } catch (error) {
      console.error("Error deleting table:", error);
      toast.error("Error deleting");
    }
  };

  const mergeTables = async () => {
    if (selectedTables.length < 2) {
      toast.error("Selecteer minstens 2 tafels om samen te voegen");
      return;
    }

    const selectedTableObjects = tables.filter(t => selectedTables.includes(t.id) && t.type !== 'decor');
    if (selectedTableObjects.length < 2) {
      toast.error("Kan alleen tafels samenvoegen, geen decor");
      return;
    }
    
    const totalCapacity = selectedTableObjects.reduce((sum, t) => sum + t.capacity, 0);
    const avgX = selectedTableObjects.reduce((sum, t) => sum + t.x, 0) / selectedTableObjects.length;
    const avgY = selectedTableObjects.reduce((sum, t) => sum + t.y, 0) / selectedTableObjects.length;
    const mergedNumbers = selectedTableObjects.map(t => t.number).sort((a, b) => a - b);

    try {
      await Promise.all(selectedTableObjects.map(t => axios.delete(`${API}/tables/${t.id}`)));
      
      await axios.post(`${API}/tables`, {
        number: mergedNumbers[0],
        capacity: totalCapacity,
        shape: 'rectangular',
        type: 'table',
        merged_from: mergedNumbers,
        x: avgX,
        y: avgY
      });

      await fetchTables();
      setSelectedTables([]);
      toast.success(`Tafels ${mergedNumbers.join('+')} samengevoegd: ${totalCapacity} personen`);
    } catch (error) {
      console.error("Error merging tables:", error);
      toast.error("Error merging tables");
      fetchTables();
    }
  };

  const updateTableStatus = async (tableId, status) => {
    try {
      await axios.put(`${API}/tables/${tableId}`, { status });
      setTables(tables.map(t => 
        t.id === tableId ? { ...t, status } : t
      ));
    } catch (error) {
      console.error("Error updating table status:", error);
      toast.error("Error updating table status");
    }
  };

  const toggleTableSelection = (tableId) => {
    setSelectedTables(prev => 
      prev.includes(tableId) 
        ? prev.filter(id => id !== tableId)
        : [...prev, tableId]
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-300">Loading...</div>
      </div>
    );
  }

  const regularTables = tables.filter(t => t.type === 'table' || !t.type);
  const barSeats = tables.filter(t => t.type === 'bar');
  const decorElements = tables.filter(t => t.type === 'decor');

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-[#0A0A0A]" data-testid="table-management-view">
      <div className="p-6 border-b border-white/10 flex justify-between items-center bg-[#1A1A1A]">
        <div>
          <h2 className="text-2xl font-serif text-gray-300" data-testid="table-plan-title">Tafelplan</h2>
          <p className="text-sm text-gray-400">Sleep om te verplaatsen • Shift+klik om te selecteren voor samenvoegen</p>
        </div>
        <div className="flex gap-2">
          {selectedTables.length >= 2 && (
            <Button 
              onClick={mergeTables}
              className="bg-purple-600 text-white hover:bg-purple-700"
              data-testid="merge-tables-button"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Voeg {selectedTables.length} tafels samen
            </Button>
          )}
          
          {/* Add Decor Dialog */}
          <Dialog open={showDecorDialog} onOpenChange={setShowDecorDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/10">
                <RectangleHorizontal className="w-4 h-4 mr-2" />
                Decor
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A1A1A] border-brand-gold/30">
              <DialogHeader>
                <DialogTitle className="text-brand-beige">Decor Element Toevoegen</DialogTitle>
                <DialogDescription className="text-gray-400">Voeg bar, muur, plant of ander decoratie element toe</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-300">Naam</Label>
                  <Input
                    value={newDecor.name}
                    onChange={(e) => setNewDecor({...newDecor, name: e.target.value})}
                    placeholder="Bar, Muur, Plant..."
                    className="bg-[#222222] border-white/20"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Breedte (px)</Label>
                    <Input
                      type="number"
                      value={newDecor.width}
                      onChange={(e) => setNewDecor({...newDecor, width: parseInt(e.target.value)})}
                      className="bg-[#222222] border-white/20"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Hoogte (px)</Label>
                    <Input
                      type="number"
                      value={newDecor.height}
                      onChange={(e) => setNewDecor({...newDecor, height: parseInt(e.target.value)})}
                      className="bg-[#222222] border-white/20"
                    />
                  </div>
                </div>
                <Button onClick={addDecorElement} className="w-full bg-brand-gold text-black hover:bg-[#F0C74A]">
                  Toevoegen
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Table Dialog */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-brand-gold text-black hover:bg-[#F0C74A]" data-testid="add-table-button">
                <Plus className="w-4 h-4 mr-2" />
                Tafel/Barkruk
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A1A1A] border-brand-gold/30">
              <DialogHeader>
                <DialogTitle className="text-brand-beige">Nieuwe Tafel of Barkruk</DialogTitle>
                <DialogDescription className="text-gray-400">Kies type, vorm en capaciteit</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-300">Type</Label>
                  <Select value={newTable.type} onValueChange={(val) => setNewTable({...newTable, type: val})}>
                    <SelectTrigger className="bg-[#222222] border-white/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#222222] border-white/20">
                      <SelectItem value="table">
                        <div className="flex items-center gap-2">
                          <Square className="w-4 h-4" /> Tafel (T)
                        </div>
                      </SelectItem>
                      <SelectItem value="bar">
                        <div className="flex items-center gap-2">
                          <Armchair className="w-4 h-4" /> Barkruk (B)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Nummer</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newTable.number}
                    onChange={(e) => setNewTable({...newTable, number: e.target.value})}
                    placeholder="bijv. 1, 2, 3..."
                    className="bg-[#222222] border-white/20"
                    data-testid="table-number-input"
                  />
                </div>
                {newTable.type === 'table' && (
                  <>
                    <div>
                      <Label className="text-gray-300">Vorm</Label>
                      <Select value={newTable.shape} onValueChange={(val) => setNewTable({...newTable, shape: val})}>
                        <SelectTrigger className="bg-[#222222] border-white/20" data-testid="shape-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#222222] border-white/20">
                          <SelectItem value="circular"><div className="flex items-center gap-2"><Circle className="w-4 h-4" /> Rond</div></SelectItem>
                          <SelectItem value="square"><div className="flex items-center gap-2"><Square className="w-4 h-4" /> Vierkant</div></SelectItem>
                          <SelectItem value="rectangular"><div className="flex items-center gap-2"><RectangleHorizontal className="w-4 h-4" /> Rechthoekig</div></SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-300">Aantal Personen</Label>
                      <Input
                        type="number"
                        min="1"
                        max="12"
                        value={newTable.capacity}
                        onChange={(e) => setNewTable({...newTable, capacity: parseInt(e.target.value)})}
                        className="bg-[#222222] border-white/20"
                        data-testid="capacity-input"
                      />
                    </div>
                  </>
                )}
                <Button onClick={addTable} className="w-full bg-brand-gold text-black hover:bg-[#F0C74A]" data-testid="confirm-add-table">
                  Toevoegen
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div 
          className="relative w-full min-h-[600px] bg-[#1A1A1A] border border-white/10 rounded-lg"
          style={{
            backgroundImage: 'radial-gradient(circle, #333 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
          data-testid="table-canvas"
        >
          <DndContext 
            sensors={sensors} 
            onDragEnd={handleDragEnd}
          >
            {tables.map((table) => (
              <DraggableTable
                key={table.id}
                table={table}
                onStatusChange={updateTableStatus}
                onEdit={setEditingTable}
                isSelected={selectedTables.includes(table.id)}
                onSelect={toggleTableSelection}
              />
            ))}
          </DndContext>
        </div>
      </div>

      <div className="p-4 border-t border-white/10 flex flex-wrap gap-4 items-center bg-[#1A1A1A]">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#D4AF37' }} />
          <span className="text-sm text-gray-400">Vrij</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#6366F1' }} />
          <span className="text-sm text-gray-400">Gereserveerd</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#B87333' }} />
          <span className="text-sm text-gray-400">Bezet</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#F0C74A' }} />
          <span className="text-sm text-gray-400">Nagerecht</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#8C5828' }} />
          <span className="text-sm text-gray-400">Rekening</span>
        </div>
        <div className="ml-auto flex gap-4 text-sm text-gray-400">
          <span>T: {regularTables.length} tafels</span>
          <span>B: {barSeats.length} barkrukken</span>
          {decorElements.length > 0 && <span>Decor: {decorElements.length}</span>}
          {selectedTables.length > 0 && <span className="text-brand-gold">{selectedTables.length} geselecteerd</span>}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingTable} onOpenChange={(open) => !open && setEditingTable(null)}>
        <DialogContent className="bg-[#1A1A1A] border-brand-gold/30">
          <DialogHeader>
            <DialogTitle className="text-brand-beige">
              {editingTable?.type === 'decor' ? `${editingTable?.name || 'Decor'} Bewerken` : 
               editingTable?.type === 'bar' ? `Barkruk B${editingTable?.number} Bewerken` :
               `Tafel T${editingTable?.number} Bewerken`}
            </DialogTitle>
          </DialogHeader>
          {editingTable && editingTable.type !== 'decor' && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-gray-300">Nummer</Label>
                <Input
                  type="number"
                  min="1"
                  value={editingTable.number}
                  onChange={(e) => setEditingTable({...editingTable, number: parseInt(e.target.value)})}
                  className="bg-[#222222] border-white/20"
                  data-testid="edit-table-number"
                />
              </div>
              {editingTable.type !== 'bar' && (
                <>
                  <div>
                    <Label className="text-gray-300">Vorm</Label>
                    <Select 
                      value={editingTable.shape} 
                      onValueChange={(val) => setEditingTable({...editingTable, shape: val})}
                    >
                      <SelectTrigger className="bg-[#222222] border-white/20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#222222] border-white/20">
                        <SelectItem value="circular">Rond</SelectItem>
                        <SelectItem value="square">Vierkant</SelectItem>
                        <SelectItem value="rectangular">Rechthoekig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Capaciteit</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={editingTable.capacity}
                      onChange={(e) => setEditingTable({...editingTable, capacity: parseInt(e.target.value)})}
                      className="bg-[#222222] border-white/20"
                    />
                  </div>
                  <div className="p-3 bg-[#222222] rounded-lg">
                    <Label className="text-gray-300 text-sm mb-2 block">Automatische Toewijzing (optioneel)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Min. groep</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="bijv. 2"
                          value={editingTable.min_party || ''}
                          onChange={(e) => setEditingTable({...editingTable, min_party: e.target.value ? parseInt(e.target.value) : null})}
                          className="bg-[#1A1A1A] border-white/20"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Max. groep</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="bijv. 4"
                          value={editingTable.max_party || ''}
                          onChange={(e) => setEditingTable({...editingTable, max_party: e.target.value ? parseInt(e.target.value) : null})}
                          className="bg-[#1A1A1A] border-white/20"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Als ingesteld, wordt deze tafel alleen automatisch toegewezen aan groepen van deze grootte</p>
                  </div>
                </>
              )}
              <div className="flex gap-2">
                <Button onClick={updateTable} className="flex-1 bg-brand-gold text-black hover:bg-[#F0C74A]">
                  Opslaan
                </Button>
                <Button 
                  onClick={() => deleteTable(editingTable.id)} 
                  variant="destructive"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Verwijderen
                </Button>
              </div>
            </div>
          )}
          {editingTable && editingTable.type === 'decor' && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-gray-300">Naam</Label>
                <Input
                  value={editingTable.name || ''}
                  onChange={(e) => setEditingTable({...editingTable, name: e.target.value})}
                  placeholder="Bar, Muur, Plant..."
                  className="bg-[#222222] border-white/20"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Breedte (px)</Label>
                  <Input
                    type="number"
                    min="30"
                    max="500"
                    value={parseInt(editingTable.width) || 150}
                    onChange={(e) => setEditingTable({...editingTable, width: `${e.target.value}px`})}
                    className="bg-[#222222] border-white/20"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Hoogte (px)</Label>
                  <Input
                    type="number"
                    min="20"
                    max="300"
                    value={parseInt(editingTable.height) || 50}
                    onChange={(e) => setEditingTable({...editingTable, height: `${e.target.value}px`})}
                    className="bg-[#222222] border-white/20"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={updateDecorElement} className="flex-1 bg-brand-gold text-black hover:bg-[#F0C74A]">
                  Opslaan
                </Button>
                <Button 
                  onClick={() => deleteTable(editingTable.id)} 
                  variant="destructive"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Verwijderen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TableManagement;
